import { IdPathParameters, SaveOperation } from '@infuse/api/common';
import { RoleImportReport, RoleTransfer } from '@infuse/iam/role/type';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

type ImportOperation =
  | SaveOperation<RoleTransfer[], RoleImportReport>
  | SaveOperation<RoleTransfer[], RoleImportReport, IdPathParameters<string>>;

/**
 * Runs an import file past the server without changing anything, so that the difference can be
 * reviewed before it is accepted.
 */
export const useGenericPreviewRoleImport = ({
  apiFn,
  forcedParentId
}: {
  apiFn: ImportOperation;
  forcedParentId?: string;
}) => {
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<RoleImportReport, unknown, { parentId?: string; roles: RoleTransfer[] }>({
    mutationFn: async ({ parentId, roles }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        return (await apiFn({ id: parentId }, roles)) as RoleImportReport;
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.roles.header.import_roles'),
          message: getAxiosErrorMessage(e, t('iam.roles.notify.preview_import_failed'))
        });
        throw e;
      }
    }
  });

  return {
    ...mutation
  };
};

/**
 * Applies a reviewed import file. The server recomputes the difference as it applies, so what comes
 * back describes what actually happened rather than what the preview predicted.
 */
export const useGenericImportRoles = ({
  apiFn,
  forcedParentId
}: {
  apiFn: ImportOperation;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<RoleImportReport, unknown, { parentId?: string; roles: RoleTransfer[] }>({
    mutationFn: async ({ parentId, roles }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        const report = (await apiFn({ id: parentId }, roles)) as RoleImportReport;

        notify({
          title: t('iam.roles.header.import_roles'),
          message: t('iam.roles.notify.import_roles_success', {
            created: report.summary.toCreate,
            updated: report.summary.toUpdate
          })
        });

        return report;
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.roles.header.import_roles'),
          message: getAxiosErrorMessage(e, t('iam.roles.notify.import_roles_failed'))
        });
        throw e;
      }
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
      await queryClient.invalidateQueries({ queryKey: ['role-permissions'] });
    }
  });

  return {
    ...mutation
  };
};
